import { Env } from '@core/env.ts'
import { isDebugAction, TrackedEventAction } from '@core/events/tracked-event-action.ts'
import { DebugEventReason } from '../enums/debug-event-reason.ts'
import { Globals } from '../globals.ts'
import { EventTracker } from '@core/events/event-manager.ts'
import { UserDebugDetails } from '../types/user-debug-details.ts'
import { getRandomString } from '@core/utils/random.ts'
import type { PushArguments } from '../types/push-sdk-legacy.ts'
import { PushSdkConfig } from '../types/push-sdk-config.ts'
import { PNException } from '@core/exceptions/pn-exception.ts'
import { PNExceptionCode } from '@core/exceptions/pn-exception-code.ts'
import { PromptDispatchMessage } from '../types/prompt-dispatch-message.ts'
import { SettingsStore } from '../storage/settings-storage-manager.ts'

function capitalizeFirstLetter(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1)
}

const DEBUG_PANEL_STYLES = `
.sw-user-debug-drawer-wrapper {
  display: block !important;
  box-sizing: border-box;
  position: absolute;
  z-index: 9999;
  bottom: 120px;
  left: -320px;
  width: 320px;
  border-radius: 4px;
  box-shadow: 0 0 12px -3px #0003;
  background: rgb(251, 251, 251);
  transition: left .24s linear;
  font-family: -apple-system, system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 13px;
  line-height: 1.15em;
  font-weight: 500;
  color: #545454;
}

.sw-user-debug-drawer-wrapper.open {
  left: 0;
}

.sw-user-debug-drawer-wrapper .sw-user-debug-shadow {
  visibility: hidden;
  border: none;
  box-shadow: none;
  outline: none;
  position: absolute;
  z-index: -1;
}

.sw-user-debug-drawer-wrapper .sw-user-debug-shadow.copying {
  visibility: visible;
}

.sw-user-debug-drawer-wrapper > .sw-user-debug-drawer > .sw-user-debug-drawer-handle {
  display: block;
  box-sizing: border-box;
  position: absolute;
  background: #0077a0;
  border-radius: 0 0 6px 6px;
  box-shadow: -1px 1px 10px -4px black;
  top: 50%;
  right: -66px;
  transform: translateY(-50%) rotate(-90deg);
  padding: 8px 20px 8px 20px;
  color: #fff;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
}

.sw-user-debug-drawer-wrapper > .sw-user-debug-drawer > .sw-user-debug-drawer-handle > .sw-user-debug-label {
  display: block;
  box-sizing: border-box;
  width: 60px;
}

.sw-user-debug-drawer-wrapper .sw-user-debug-drawer .sw-user-debug-drawer-content {
  font-size: 12px;
  padding: 20px;
}

.sw-user-debug-drawer-wrapper .sw-user-debug-drawer .sw-user-debug-drawer-content .sw-user-debug-label {
  color: #222;
  font-weight: bold;
  display: block;
  box-sizing: border-box;
  margin-bottom: 4px;
}

.sw-user-debug-drawer-wrapper .sw-user-debug-drawer .sw-user-debug-drawer-content .sw-user-debug-value {
  display: block;
  word-break: keep-all;
  box-sizing: border-box;
  border: 1px #0002 solid;
  border-radius: 3px;
  padding: 6px;
  margin-bottom: 1em;
  width: 100%;
  background: #F2F2F2;
  font-size: 1em;
  line-height: 14px;
}

.sw-user-debug-drawer-wrapper .sw-user-debug-drawer .sw-user-debug-drawer-content .sw-user-debug-actions {
  display: flex;
  align-content: flex-end;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}

.sw-user-debug-drawer-wrapper .sw-user-debug-drawer .sw-user-debug-drawer-content .sw-user-debug-actions > .sw-user-debug-btn {
  display: block;
  border: none;
  padding: 6px 14px;
  border-radius: 3px;
  transition: all .24s;
  cursor: pointer;
}

.sw-user-debug-drawer-wrapper .sw-user-debug-drawer .sw-user-debug-drawer-content .sw-user-debug-actions > .sw-user-debug-btn.copy {
  background: #0077a0;
  color: #fff;
  transition: all 1s;
}

.sw-user-debug-drawer-wrapper .sw-user-debug-drawer .sw-user-debug-drawer-content .sw-user-debug-actions > .sw-user-debug-btn.copy.success {
  background: green;
}

.sw-user-debug-drawer-wrapper .sw-user-debug-drawer .sw-user-debug-drawer-content .sw-user-debug-actions > .sw-user-debug-btn.copy.success span {
  font-size: 12px;
}

.sw-user-debug-drawer-wrapper .sw-user-debug-drawer .sw-user-debug-drawer-content .sw-user-debug-actions > .sw-user-debug-btn.close {
  background: unset;
  color: #888888;
}
`.trim()

export class DebugController {
    protected debugPanelElement: HTMLElement | undefined

    public constructor(
        readonly settingsStore: SettingsStore,
        readonly eventManager: EventTracker,
    ) {}

    public async _getIsEventsEnabled(): Promise<boolean> {
        let flags: string[] = []

        const injected = Globals._injectedDomainConfig
        if (injected) {
            flags = injected.flags
        } else {
            const domain = await this.settingsStore._getDomainConfig()
            if (domain?.value.flags) {
                flags = domain.value.flags
            }
        }

        return flags.includes(Globals._sdkDebugEventsEnabledFlag)
    }

    public _shouldShowDebugPanel(): boolean {
        return this._currLocation && /(^|[?&#])(push(ly)?_debug(73485)?)=1(?=[&#]|$)/i.test(this._currLocation.href)
    }

    public get _currLocation(): Location {
        return Env._location
    }

    public get _document(): Document {
        return Env._document
    }

    public get _clipboard(): Clipboard {
        return Env._navigator.clipboard
    }

    public get _userAgent(): string {
        return Env._navigator.userAgent
    }

    public async _hideDebugPanel(): Promise<void> {
        if (!this.debugPanelElement) {
            return
        }

        try {
            this._document.body.removeChild(this.debugPanelElement)
        } catch {}
    }

    public async _showDebugPanel({
        userId,
        subscriberStatus,
        endpoint,
        externalId,
        browserPermission,
    }: UserDebugDetails): Promise<void> {
        const doc = this._document
        const userDetailString =
            `Pushly ID: ${userId}, Subscriber State: ${subscriberStatus}, External ID: ${externalId}, Endpoint: ${endpoint}, Browser Permissions: ${browserPermission}, UserAgent: ${this._userAgent}`.trim()

        // set ids for elements to control - prevent client site interactions from possibly occurring
        const handleId = `pushly_debug_handle_${getRandomString(12)}`
        const closeBtnId = `pushly_debug_close_${getRandomString(12)}`
        const copyBtnId = `pushly_debug_copy_${getRandomString(12)}`
        const shadowTextId = `pushly_debug_shadow_${getRandomString(12)}`

        const wrapper = this.debugPanelElement ?? doc.createElement('div')
        wrapper.classList.add('sw-user-debug-drawer-wrapper')
        const style = doc.createElement('style')
        style.textContent = DEBUG_PANEL_STYLES

        const drawer = doc.createElement('div')
        drawer.classList.add('sw-user-debug-drawer')

        const handle = doc.createElement('div')
        handle.id = handleId
        handle.classList.add('sw-user-debug-drawer-handle')

        const handleLabel = doc.createElement('span')
        handleLabel.classList.add('sw-user-debug-label')
        handleLabel.textContent = 'Push SDK'
        handle.appendChild(handleLabel)

        const content = doc.createElement('div')
        content.classList.add('sw-user-debug-drawer-content')

        const appendField = (labelText: string, valueText: string) => {
            const label = doc.createElement('div')
            label.classList.add('sw-user-debug-label')
            label.textContent = labelText

            const value = doc.createElement('input')
            value.classList.add('sw-user-debug-value')
            value.value = valueText
            value.disabled = true

            content.append(label, value)
        }

        appendField('Pushly ID', userId ?? 'Not set')
        appendField('External ID', externalId ?? 'Not set')

        const subscriptionStateLabel = doc.createElement('div')
        subscriptionStateLabel.classList.add('sw-user-debug-label')
        subscriptionStateLabel.textContent = 'Subscription State'

        const subscriptionState = doc.createElement('span')
        subscriptionState.textContent = capitalizeFirstLetter(subscriberStatus)
        content.append(subscriptionStateLabel, subscriptionState)

        const actions = doc.createElement('div')
        actions.classList.add('sw-user-debug-actions')

        const closeBtn = doc.createElement('button')
        closeBtn.id = closeBtnId
        closeBtn.classList.add('sw-user-debug-btn', 'close')
        closeBtn.type = 'button'
        closeBtn.textContent = 'Close'

        const copyBtn = doc.createElement('button')
        copyBtn.id = copyBtnId
        copyBtn.classList.add('sw-user-debug-btn', 'copy')
        copyBtn.type = 'button'
        copyBtn.textContent = 'Copy'

        actions.append(closeBtn, copyBtn)
        content.appendChild(actions)
        drawer.append(handle, content)
        wrapper.replaceChildren(style, drawer)

        const handleDebugDrawerState = async () => {
            if (wrapper.classList.contains('open')) {
                wrapper.classList.remove('open')
            } else {
                wrapper.classList.add('open')
            }
        }

        handle.addEventListener('click', handleDebugDrawerState)
        closeBtn.addEventListener('click', handleDebugDrawerState)

        const handleCopy = () => {
            let success = false
            try {
                this._clipboard.writeText(userDetailString)
                success = true
            } catch (_) {
                try {
                    const shadowText = doc.createElement('input')
                    doc.body.appendChild(shadowText)
                    shadowText.id = shadowTextId
                    shadowText.classList.add('sw-user-debug-shadow')
                    shadowText.type = 'text'
                    shadowText.defaultValue = userDetailString

                    shadowText.classList.add('copying')
                    shadowText.select()
                    success = doc.execCommand('copy')
                    shadowText.classList.remove('copying')
                } catch (_) {}
            }

            if (success) {
                copyBtn.classList.add('success')
                copyBtn.replaceChildren('Copied ')
                const checkIcon = doc.createElement('span')
                checkIcon.textContent = '✓'
                copyBtn.appendChild(checkIcon)
                setTimeout(() => {
                    copyBtn.classList.remove('success')
                    copyBtn.textContent = 'Copy'
                }, 1000)
            }
        }

        copyBtn.addEventListener('click', handleCopy)

        if (!this.debugPanelElement) {
            doc.body.appendChild(wrapper)
            this.debugPanelElement = wrapper
        }
    }

    public _debug(action: TrackedEventAction, data: any = {}, forceSend: boolean = false) {
        const debug = async () => {
            if (isDebugAction(action) && (forceSend || (await this._getIsEventsEnabled()))) {
                this.eventManager._track(action, data)
            }
        }

        debug().then()
    }

    public _debugPromptNotShown(reason: DebugEventReason, details?: string) {
        const data: Record<string, any> = { [Globals._tevDataPromptNotShownKey]: reason }
        if (details) {
            data[Globals._tevFieldErrorMessage] = details
        }

        return this._debug(TrackedEventAction.PROMPT_NOT_SHOWN, data)
    }

    // Debug Unknowns
    // These handlers capture unexpected usage of the new SDK

    public _debugUnknownAsyncBrowserCommand(commandArgs: PushArguments) {
        return this.eventManager._track(
            new PNException(PNExceptionCode.UNKNOWN_ASYNC_COMMAND, null, String(commandArgs[0])),
            {
                async_command: commandArgs,
            },
        )
    }

    public _debugUnknownConfigurationKeys(config: PushSdkConfig) {
        return this.eventManager._track(new PNException(PNExceptionCode.UNKNOWN_CONFIG_KEYS), {
            push_sdk_config: config,
        })
    }

    public _debugUnknownContextKey(key: string) {
        return this.eventManager._track(new PNException(PNExceptionCode.UNKNOWN_CONTEXT_KEY, null, key), {
            context_key: key,
        })
    }

    public _debugUnknownPromptMessage(message: PromptDispatchMessage) {
        return this.eventManager._track(new PNException(PNExceptionCode.UNKNOWN_PROMPT_MESSAGE), {
            prompt_message: message,
        })
    }
}
